import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Modal,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    collection,
    doc,
    onSnapshot,
    addDoc,
    query,
    orderBy,
    updateDoc,
    getDoc,
} from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import axios from 'axios';
import notificationService from './services/notificationService';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SendIcon from '@mui/icons-material/Send';
import MapIcon from '@mui/icons-material/Map';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloseIcon from '@mui/icons-material/Close';
import LocalParkingIcon from '@mui/icons-material/LocalParking'; // Icon for "I have parked your car"
import VpnKeyIcon from '@mui/icons-material/VpnKey'; // Icon for "I am returning your keys"
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // Icon for parking fee
import { Alert } from '@mui/material'; // Import Alert component

const MessageContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isUser', // Prevent isUser from being forwarded to the DOM
})(({ isUser }) => ({
    display: 'flex',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    marginBottom: '16px',
    padding: '0 16px',
}));

const MessageBubble = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isUser' && prop !== 'isSystem' && prop !== 'isError', // Prevent isUser and isSystem from being forwarded to the DOM
})(({ isUser, isSystem, isError }) => ({
    maxWidth: '100%',
    padding: '12px 16px',
    borderRadius: '16px',
    backgroundColor: isError ? '#FEE3E3' : isSystem ? '#E3F2FD' : (isUser ? '#113524' : '#F4F4F4'),
    color: isError ? '#DB2C2C' : isSystem ? '#1976D2' : (isUser ? '#fff' : '#000'),
    fontSize: '16px',
    wordBreak: 'break-word',
    border: isError ? '1px solid #EF4545' : isSystem ? '1px solid #90CAF9' : 'none',
    fontStyle: isSystem ? 'italic' : 'normal',
    '& a': {
        color: isUser ? '#ffffff' : '#0066cc',
        textDecoration: 'underline',
        display: 'block',
        marginTop: '8px',
    },
}));

const SenderLabel = styled(Typography)({
    fontSize: '13px',
    color: '#666',
    marginBottom: '4px',
    fontWeight: 400,
});

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '25px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
            borderColor: '#113524',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#113524',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '12px 20px',
    },
});

const ActiveConversation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { conversationId: routeConversationId, isGuest: routeIsGuest } =
        location.state || {};
    const storedGuestConversationId =
        sessionStorage.getItem('guestConversationId') ||
        localStorage.getItem('guestConversationId');
    const storedGuestUserRaw =
        sessionStorage.getItem('guestUser') || localStorage.getItem('guestUser');
    const isGuest =
        routeIsGuest ??
        (() => {
            try {
                return !!(storedGuestUserRaw && JSON.parse(storedGuestUserRaw));
            } catch (e) {
                return false;
            }
        })();
    const conversationId =
        routeConversationId || (isGuest ? storedGuestConversationId : undefined);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [conversationStatus, setConversationStatus] = useState('active');
    const [valetName, setValetName] = useState(null);
    const [valetLocation, setValetLocation] = useState(null);
    const [carStatus, setCar_status] = useState('unparked');
    const [guestUser, setGuestUser] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [collectOtp, setCollectOtp] = useState(null); // From Firestore collectOtp field
    const [collectOtpVerified, setCollectOtpVerified] = useState(false); // From Firestore
    const [otpInput, setOtpInput] = useState(''); // For storing OTP entered by the guest
    const [otpError, setOtpError] = useState(''); // OTP validation error message
    const [isModalOpen, setIsModalOpen] = useState(false); // OTP modal visibility state
    const [isOtpVerifying, setIsOtpVerifying] = useState(false);
    const [otpModalDismissed, setOtpModalDismissed] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
        useState(false); // Confirmation modal visibility state
    const [isFeeModalOpen, setIsFeeModalOpen] = useState(false); // Modal for entering parking fee
    const [parkingFee, setParkingFee] = useState(''); // State for parking fee input
    const [paymentLinkGenerated, setPaymentLinkGenerated] = useState(false);
    const [hasParkingFee, setHasParkingFee] = useState(false); // State to track if parking fee exists

    // Ref for the message container to scroll to the bottom
    const messagesEndRef = useRef(null);

    // Scroll to the bottom whenever messages are updated
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const handleVerifyOtp = async () => {
        // Guest verifies collect-keys OTP against Firestore collectOtp field
        if (!otpInput.trim()) {
            setOtpError('Please enter the OTP');
            return;
        }

        if (isOtpVerifying) {
            return;
        }

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setOtpError('No internet connection. Please reconnect and try again.');
            return;
        }

        setIsOtpVerifying(true);
        setOtpError('');

        try {
            const conversationRef = doc(db, 'conversations', conversationId);
            const conversationDoc = await getDoc(conversationRef);

            if (!conversationDoc.exists()) {
                setOtpError('Conversation not found.');
                return;
            }

            const storedOtp = conversationDoc.data().collectOtp;

            if (otpInput.trim() === storedOtp?.toString()) {
                // OTP matches — mark as verified in Firestore
                await updateDoc(conversationRef, { collectOtpVerified: true });

                // Send success system message
                const messagesRef = collection(
                    db,
                    `conversations/${conversationId}/messages`
                );
                await addDoc(messagesRef, {
                    senderId: 'system',
                    text: 'OTP for collecting keys is verified',
                    messageType: 'otp_verified',
                    isSystemMessage: true,
                    createdAt: new Date(),
                });

                setIsModalOpen(false);
                setOtpInput('');
                setOtpError('');
                setOtpModalDismissed(false);
            } else {
                setOtpError('Invalid OTP');

                // Send error message visible to both
                const messagesRef = collection(
                    db,
                    `conversations/${conversationId}/messages`
                );
                await addDoc(messagesRef, {
                    senderId: 'system',
                    text: 'Invalid OTP',
                    isSystemMessage: true,
                    createdAt: new Date(),
                });
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                setOtpError(
                    'No internet connection. Please reconnect and try again.'
                );
            } else {
                setOtpError('Network error. Please try again.');
            }
        } finally {
            setIsOtpVerifying(false);
        }
    };

    const [timeInquiries, setTimeInquiries] = useState(0);
    const FASTAPI_URL =
        process.env.REACT_APP_FASTAPI_URL || 'http://localhost:8000'; // Add fallback URL

    // Google Maps Geolocation API Key
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    // Function to get location using Google Maps Geolocation API
    const getLocationWithGoogleMaps = async () => {
        try {
            const response = await axios.post(
                `https://www.googleapis.com/geolocation/v1/geolocate?key=${GOOGLE_MAPS_API_KEY}`,
                {
                    considerIp: true, // Fallback to IP-based location if no other data is available
                }
            );
            const { lat, lng } = response.data.location;
            return { lat, lng };
        } catch (error) {
            console.error(
                'Error getting location with Google Maps API:',
                error
            );
            return null;
        }
    };

    const handleLocationInquiry = async () => {
        if (userRole === 'valet' && conversationStatus === 'active') {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const location = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        setValetLocation(location);
                        console.log('GPS location:', location);

                        // Commented out chatbot backend call
                        /* try {
                            const response = await axios.post(
                                `${FASTAPI_URL}/update_valet_location`,
                                location
                            );
                            console.log('response', response);
                            const messagesRef = collection(
                                db,
                                `conversations/${conversationId}/messages`
                            );

                            if (response.data.message) {
                                const botReply = {
                                    senderId: 'bot',
                                    text: response.data.message,
                                    createdAt: new Date(),
                                };
                                await addDoc(messagesRef, botReply);
                            }
                        } catch (error) {
                            console.error(
                                'Error updating valet location:',
                                error
                            );
                        } */
                        
                        // Just add a message that location was shared
                        const messagesRef = collection(
                            db,
                            `conversations/${conversationId}/messages`
                        );
                        await addDoc(messagesRef, {
                            senderId: 'bot',
                            text: 'Valet location has been shared.',
                            createdAt: new Date(),
                        });
                    },
                    async (error) => {
                        console.error('Error getting GPS location:', error);
                        console.log('Falling back to Google Maps API...');

                        const location = await getLocationWithGoogleMaps();
                        if (location) {
                            setValetLocation(location);
                            console.log('Google Maps location:', location);

                            try {
                                const response = await axios.post(
                                    `${FASTAPI_URL}/update_valet_location`,
                                    location
                                );
                                console.log('response', response);
                                const messagesRef = collection(
                                    db,
                                    `conversations/${conversationId}/messages`
                                );

                                if (response.data.message) {
                                    const botReply = {
                                        senderId: 'bot',
                                        text: response.data.message,
                                        createdAt: new Date(),
                                    };
                                    await addDoc(messagesRef, botReply);
                                }
                            } catch (error) {
                                console.error(
                                    'Error updating valet location:',
                                    error
                                );
                            }
                        } else {
                            console.error(
                                'Failed to get location from Google Maps API.'
                            );
                        }
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000, // 10 seconds
                        maximumAge: 0,
                    }
                );
            } else {
                console.log('Falling back to Google Maps API...');
                const location = await getLocationWithGoogleMaps();
                if (location) {
                    setValetLocation(location);
                    console.log('Google Maps location:', location);

                    try {
                        const response = await axios.post(
                            `${FASTAPI_URL}/update_valet_location`,
                            location
                        );
                        console.log('response', response);
                        const messagesRef = collection(
                            db,
                            `conversations/${conversationId}/messages`
                        );

                        if (response.data.message) {
                            const botReply = {
                                senderId: 'bot',
                                text: response.data.message,
                                createdAt: new Date(),
                            };
                            await addDoc(messagesRef, botReply);
                        }
                    } catch (error) {
                        console.error('Error updating valet location:', error);
                    }
                } else {
                    console.error(
                        'Failed to get location from Google Maps API.'
                    );
                }
            }
        }
    };

    useEffect(() => {
        // Handle guest user
        if (isGuest) {
            if (storedGuestUserRaw) {
                try {
                    const guest = JSON.parse(storedGuestUserRaw);
                    setGuestUser(guest);
                    setUserRole('customer');
                    setCurrentUserId(guest.id || 'guest');
                } catch (e) {
                    console.error(
                        'Failed to parse guestUser from sessionStorage:',
                        e
                    );
                }
            }
        }
        
        // Request notification permission for customers
        if (userRole === 'customer' || isGuest) {
            notificationService.requestPermission().then(granted => {
                if (granted) {
                    notificationService.subscribeToPush().then(subscription => {
                        if (subscription && currentUserId && currentUserId !== 'guest') {
                            notificationService.saveSubscription(currentUserId, subscription);
                        }
                    });
                }
            });
        }
        
        let unsubscribeConversation = null;
        let unsubscribeMessages = null;

        const setupListeners = async () => {
            if (!conversationId) {
                setError('Conversation ID not provided.');
                return;
            }

            try {
                // Set up conversation listener
                const conversationRef = doc(
                    db,
                    'conversations',
                    conversationId
                );
                console.log('conversationRef', conversationRef);

                unsubscribeConversation = onSnapshot(conversationRef, (doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        setConversationStatus(data.status || 'active');
                        setValetName(data.valetName || null);
                        setHasParkingFee(!!data.parkingFee); // Update hasParkingFee state

                        // Track collect-keys OTP state from Firestore
                        setCollectOtp(data.collectOtp || null);
                        setCollectOtpVerified(!!data.collectOtpVerified);

                        if ((data.collectOtp || null) !== collectOtp) {
                            setOtpModalDismissed(false);
                        }

                        // Auto-show OTP modal for guest when valet generates collect OTP
                        if (
                            data.collectOtp &&
                            !data.collectOtpVerified &&
                            (userRole === 'customer' || isGuest) &&
                            !otpModalDismissed
                        ) {
                            setIsModalOpen(true);
                        }

                        // Redirect both users when conversation is completed
                        if (data.status === 'completed') {
                            if (isGuest) {
                                sessionStorage.removeItem('guestConversationId');
                                sessionStorage.removeItem('guestIsRetrieval');
                                localStorage.removeItem('guestConversationId');
                                localStorage.removeItem('guestIsRetrieval');
                                navigate('/');
                            } else {
                                navigate('/home');
                            }
                        }
                    } else {
                        setError('Conversation not found.');
                    }
                });

                // Set up messages listener
                const messagesRef = collection(
                    db,
                    `conversations/${conversationId}/messages`
                );
                const messagesQuery = query(messagesRef, orderBy('createdAt'));
                unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
                    const msgs = snapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    }));
                    
                    // Check for new messages and show notifications
                    if (messages.length > 0 && msgs.length > messages.length) {
                        const newMessages = msgs.slice(messages.length);
                        newMessages.forEach(msg => {
                            // Show notification for valet messages to customers
                            if (userRole === 'customer' && msg.senderId !== currentUserId && msg.senderId !== 'bot') {
                                const title = 'New message from valet';
                                const body = msg.text.substring(0, 100) + (msg.text.length > 100 ? '...' : '');
                                
                                // Only show notification if app is not visible
                                if (!notificationService.isAppVisible()) {
                                    notificationService.showNotification(title, body, {
                                        conversationId,
                                        url: '/active-chat'
                                    });
                                }
                                
                                // Always play sound for new messages
                                notificationService.playNotificationSound();
                            }
                        });
                    }
                    
                    setMessages(msgs);

                    // Check if the user is a customer or guest and no messages have been sent
                    if (
                        msgs.length === 0 &&
                        (userRole === 'customer' || userRole === 'guest') &&
                        valetName
                    ) {
                        setNewMessage(
                            `Hey there, I'm using your valet service. My valet is ${valetName} and he's parking the car for me.`
                        );
                    }
                });

                // Fetch user role
                if (auth.currentUser) {
                    const userDoc = doc(db, 'users', auth.currentUser.uid);
                    const userData = await getDoc(userDoc);
                    if (userData.exists()) {
                        setUserRole(userData.data().role);
                        setCurrentUserId(auth.currentUser.uid);
                    }
                } else if (!isGuest) {
                    setUserRole('guest');
                }
                // For guest users, role and userId are already set above
            } catch (err) {
                console.error('Error setting up listeners:', err);
                setError(
                    'An error occurred while setting up the conversation.'
                );
            }
        };

        setupListeners();

        // Cleanup function
        return () => {
            if (unsubscribeConversation) {
                unsubscribeConversation();
            }
            if (unsubscribeMessages) {
                unsubscribeMessages();
            }
        };
    }, [conversationId, navigate, valetName, userRole]);

    const [lastProcessedMessageId, setLastProcessedMessageId] = useState(null);

    useEffect(() => {
        const lastMessage = messages[messages.length - 1];

        const locationPhrases = [
            'grabbing location details for you',
            'your car is currently being parked',
            'your car is securely parked at the location',
            'status updated: car has been parked securely',
            'grabbing my location details for you',
            'status updated: car has been parked securely. your otp is',
            'please verify the otp with the valet to retrieve your keys',
        ];

        // Only process messages from the bot and if it hasn't been processed yet
        if (
            lastMessage?.senderId === 'bot' &&
            lastMessage?.id !== lastProcessedMessageId
        ) {
            console.log('Processing new bot message:', lastMessage.text);
            const messageText = lastMessage?.text.toLowerCase();

            // Check if it's a car parked message with OTP
            const hasParkedStatus = messageText.includes(
                'status updated: car has been parked securely'
            );
            const hasOTP = messageText.includes('your otp is');

            if (hasParkedStatus && hasOTP) {
                console.log(
                    'Car parked with OTP detected - Starting location update'
                );
                handleLocationInquiry(); // Only handle location
                (async () => {
                    try {
                        const otpMatch =
                            messageText.match(/your otp is (\d+)/i);
                        const otpNumber = otpMatch
                            ? parseInt(otpMatch[1])
                            : null;

                        if (!otpNumber) {
                            console.error(
                                'Could not extract OTP number from message'
                            );
                            return;
                        }

                        console.log(
                            'Making car_parked API call with OTP:',
                            otpNumber
                        );
                        // Commented out chatbot backend call
                        /* const response = await axios.post(
                            `${FASTAPI_URL}/car_parked`,
                            {
                                otp_number: otpNumber,
                            }
                        );
                        console.log('API response:', response.data); */

                        // Update car status locally
                        const conversationRef = doc(
                            db,
                            'conversations',
                            conversationId
                        );
                        await updateDoc(conversationRef, {
                            carStatus: 'parked',
                        });
                        setCar_status('parked');

                        // Remove payment link generation from here
                    } catch (error) {
                        console.error(
                            'Error in car_parked flow:',
                            error.response?.data || error
                        );
                    }
                })();
            }
            // Handle other location inquiries
            else if (
                locationPhrases.some((phrase) =>
                    messageText.includes(phrase.toLowerCase())
                )
            ) {
                console.log(
                    'Location phrase detected - triggering location inquiry only'
                );
                handleLocationInquiry();
            }

            setLastProcessedMessageId(lastMessage.id);
        }
    }, [
        messages,
        userRole,
        conversationStatus,
        conversationId,
        lastProcessedMessageId,
    ]);

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;

        try {
            console.log('Starting to send message...'); // Debug log
            const messagesRef = collection(
                db,
                `conversations/${conversationId}/messages`
            );
            const senderId = auth.currentUser
                ? auth.currentUser.uid
                : guestUser?.id || 'guest_user';

            const userMessage = {
                senderId,
                text: newMessage,
                createdAt: new Date(),
            };
            console.log('Adding user message to Firebase:', userMessage); // Debug log
            await addDoc(messagesRef, userMessage);

            // Commented out chatbot backend call
            /* // Map the userRole to the expected backend values
            let senderType = 'customer';
            if (userRole === 'valet') {
                senderType = 'valet';
            } else if (userRole === 'guest' || userRole === 'customer') {
                senderType = 'customer';
            }

            // Create the request body with all required fields
            const requestBody = {
                text: newMessage,
                sender: senderType,
                car_status: carStatus || 'unparked',
                time_inquiries: timeInquiries || 0,
                OTP: null, // OTP now managed via Firestore collectOtp field
            };

            console.log('Sending request to backend:', requestBody); // Debug log
            const response = await axios.post(
                `${FASTAPI_URL}/process_message`,
                requestBody
            );
            console.log('Received response from backend:', response.data); // Debug log

            if (response.data.message) {
                // Check if the reply contains parking duration information
                const minutesMatch = response.data.message.match(
                    /park your car for (\d+) (minutes?|mins?|min)/i
                );
                const hoursMatch = response.data.message.match(
                    /park your car for (\d+) (hours?|hrs?)/i
                );

                let durationMinutes = 0;
                if (minutesMatch) {
                    durationMinutes = parseInt(minutesMatch[1]);
                } else if (hoursMatch) {
                    durationMinutes = parseInt(hoursMatch[1]) * 60;
                }

                if (durationMinutes > 0) {
                    // Update the conversation document with parking details
                    const conversationRef = doc(
                        db,
                        'conversations',
                        conversationId
                    );
                    await updateDoc(conversationRef, {
                        parkingDuration: durationMinutes,
                        parkingStartTime: new Date().toISOString(),
                    });
                }

                // Update conversation document with any status or inquiry changes
                const conversationRef = doc(
                    db,
                    'conversations',
                    conversationId
                );
                const updates = {};

                if (response.data.carStatus) {
                    updates.carStatus = response.data.carStatus;
                    setCar_status(response.data.carStatus);
                }
                if (response.data.time_inquiries !== undefined) {
                    updates.time_inquiries = response.data.time_inquiries;
                }

                if (Object.keys(updates).length > 0) {
                    await updateDoc(conversationRef, updates);
                }

                // Add these console logs to debug
                console.log('Response carStatus:', response.data.carStatus);
                console.log('Current carStatus state:', carStatus);

                const botReply = {
                    senderId: 'bot',
                    text: response.data.message,
                    createdAt: new Date(),
                };
                await addDoc(messagesRef, botReply);
            } */

            setNewMessage('');
        } catch (err) {
            console.error('Error sending message:', err);
            setError('Failed to send message. Please try again.');
        }
    };

    const handleCallValet = () => {
        console.log('Call Valet button clicked');
    };

    const handleEndConversation = async () => {
        try {
            const conversationRef = doc(db, 'conversations', conversationId);
            await updateDoc(conversationRef, { status: 'completed' });
            if (isGuest) {
                sessionStorage.removeItem('guestConversationId');
                sessionStorage.removeItem('guestIsRetrieval');
                localStorage.removeItem('guestConversationId');
                localStorage.removeItem('guestIsRetrieval');
                navigate('/');
            } else {
                navigate('/home'); // Redirect to home after ending the conversation
            }
        } catch (err) {
            console.error('Error ending conversation:', err);
            setError('Failed to end conversation. Please try again.');
        }
    };

    const handleGeneratePaymentLink = async () => {
        try {
            const conversationRef = doc(db, 'conversations', conversationId);
            const conversationDoc = await getDoc(conversationRef);
            if (conversationDoc.exists()) {
                const durationHours = Math.ceil(
                    conversationDoc.data().parkingDuration / 60
                );
                const paymentAmountCents =
                    2000 + conversationDoc.data().parkingFee;

                if (valetLocation) {
                    const updates = {
                        lat: valetLocation.lat,
                        lng: valetLocation.lng,
                    };
                    await updateDoc(conversationRef, updates);
                }

                // Commented out chatbot backend call
                /* const response = await axios.post(
                    `${FASTAPI_URL}/create-payment-link`,
                    {
                        amount: paymentAmountCents,
                        currency: 'usd',
                        description: 'Parking Service Fee',
                    }
                );

                const paymentLink = response.data.url; */
                
                // Create a dummy payment link for now
                const paymentLink = `https://checkout.stripe.com/pay/example_${paymentAmountCents}`;

                const messagesRef = collection(
                    db,
                    `conversations/${conversationId}/messages`
                );
                await addDoc(messagesRef, {
                    senderId: 'bot',
                    text: 'Please complete payment here:',
                    link: paymentLink,
                    createdAt: new Date(),
                });
            } else {
                console.error(
                    'Parking duration not set in the conversation document.'
                );
            }
        } catch (error) {
            console.error('Error generating payment link:', error);
        }
    };

    const renderMessageWithLinks = (text) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split(urlRegex).map((part, index) =>
            urlRegex.test(part) ? (
                <a
                    key={index}
                    href={part}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                        color: '#0066cc',
                        textDecoration: 'underline',
                    }}>
                    {part}
                </a>
            ) : (
                part
            )
        );
    };

    const confirmEndConversation = () => {
        handleEndConversation();
        setIsConfirmationModalOpen(false); // Close the confirmation modal
    };

    const cancelEndConversation = () => {
        setIsConfirmationModalOpen(false); // Close the confirmation modal
    };

    const handleParkedCar = async () => {
        if (!hasParkingFee) {
            alert('Please fill in the parking meter fee first.');
            return;
        }

        try {
            const messagesRef = collection(
                db,
                `conversations/${conversationId}/messages`
            );
            const senderId = auth.currentUser
                ? auth.currentUser.uid
                : guestUser?.id || 'guest_user';

            const userMessage = {
                senderId,
                text: 'I have parked your car.',
                createdAt: new Date(),
            };
            await addDoc(messagesRef, userMessage);

            let senderType = 'valet';

            const requestBody = {
                text: 'I have parked your car.',
                sender: senderType,
                car_status: carStatus || 'unparked',
                time_inquiries: timeInquiries || 0,
                OTP: null, // OTP now managed via Firestore collectOtp field
            };

            console.log('Sending request to backend:', requestBody);
            const response = await axios.post(
                `${FASTAPI_URL}/process_message`,
                requestBody
            );
            console.log('Received response from backend:', response.data);

            if (response.data.message) {
                if (response.data.carStatus) {
                    const conversationRef = doc(
                        db,
                        'conversations',
                        conversationId
                    );
                    await updateDoc(conversationRef, {
                        carStatus: response.data.carStatus,
                    });
                    setCar_status(response.data.carStatus);
                }

                const botReply = {
                    senderId: 'bot',
                    text: response.data.message,
                    createdAt: new Date(),
                };
                await addDoc(messagesRef, botReply);

                // Generate payment link here if car is parked
                if (response.data.carStatus === 'parked') {
                    await handleGeneratePaymentLink();
                }
            }
        } catch (error) {
            console.error('Error sending parked car message:', error);
        }
    };

    const handleReturningKeys = async () => {
        try {
            const messagesRef = collection(
                db,
                `conversations/${conversationId}/messages`
            );
            const senderId = auth.currentUser
                ? auth.currentUser.uid
                : guestUser?.id || 'guest_user';

            const userMessage = {
                senderId,
                text: 'I am returning your keys.',
                createdAt: new Date(),
            };
            await addDoc(messagesRef, userMessage);

            // Map the userRole to the expected backend values
            let senderType = 'valet'; // Assuming the valet is sending this message

            // Create the request body with all required fields
            const requestBody = {
                text: 'I am returning your keys.',
                sender: senderType,
                car_status: carStatus || 'unparked',
                time_inquiries: timeInquiries || 0,
                OTP: null, // OTP now managed via Firestore collectOtp field
            };

            console.log('Sending request to backend:', requestBody); // Debug log
            const response = await axios.post(
                `${FASTAPI_URL}/process_message`,
                requestBody
            );
            console.log('Received response from backend:', response.data); // Debug log

            if (response.data.message) {
                const botReply = {
                    senderId: 'bot',
                    text: response.data.message,
                    createdAt: new Date(),
                };
                await addDoc(messagesRef, botReply);
            }
        } catch (error) {
            console.error('Error sending returning keys message:', error);
        }
    };

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color='error' align='center'>
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100%',
                bgcolor: '#FFFFFF',
                overflowX: 'hidden',
            }}>
            {/* OTP Verification Modal — Guest enters collect-keys OTP */}
            <Modal open={isModalOpen} onClose={() => { setIsModalOpen(false); setOtpError(''); setOtpModalDismissed(true); }}>
                <Box
                    sx={{
                        width: 300,
                        padding: 3,
                        backgroundColor: 'white',
                        margin: 'auto',
                        marginTop: '20%',
                        borderRadius: 2,
                        textAlign: 'center',
                    }}>
                    <Typography variant='h6'>OTP Verification</Typography>
                    <Typography variant='body2' sx={{ color: '#666', mt: 1 }}>
                        Enter OTP to hand over key
                    </Typography>
                    <TextField
                        fullWidth
                        margin='normal'
                        label='OTP'
                        value={otpInput}
                        onChange={(e) => {
                            setOtpInput(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                            setOtpError('');
                        }}
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        error={!!otpError}
                        helperText={otpError}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                            variant='outlined'
                            onClick={() => { setIsModalOpen(false); setOtpError(''); setOtpInput(''); setOtpModalDismissed(true); }}
                            disabled={isOtpVerifying}
                            sx={{ flex: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleVerifyOtp}
                            disabled={otpInput.length < 6 || isOtpVerifying}
                            sx={{ flex: 1, backgroundColor: '#113524' }}>
                            {isOtpVerifying ? (
                                <CircularProgress size={18} sx={{ color: '#fff' }} />
                            ) : (
                                'Verify'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                open={isConfirmationModalOpen}
                onClose={() => setIsConfirmationModalOpen(false)}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
                    }}>
                    <Box
                        sx={{
                            width: '80%',
                            backgroundColor: '#FFFFFF',
                            borderRadius: 2,
                            padding: 3,
                            textAlign: 'center',
                        }}>
                        <Typography variant='h6' sx={{ mb: 2 }}>
                            Are you sure you want to end this conversation?
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}>
                            <Button
                                variant='contained'
                                onClick={confirmEndConversation}
                                sx={{
                                    backgroundColor: '#113524',
                                    color: '#fff',
                                    flex: 1,
                                }}>
                                OK
                            </Button>
                            <Button
                                variant='contained'
                                onClick={cancelEndConversation}
                                sx={{
                                    backgroundColor: '#d32f2f',
                                    color: '#fff',
                                    flex: 1,
                                }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Parking Fee Modal */}
            <Modal
                open={isFeeModalOpen}
                onClose={() => setIsFeeModalOpen(false)}>
                <Box
                    sx={{
                        width: 300,
                        padding: 3,
                        backgroundColor: 'white',
                        margin: 'auto',
                        marginTop: '20%',
                        borderRadius: 2,
                        textAlign: 'center',
                    }}>
                    <Typography variant='h6'>Enter Parking Fee</Typography>
                    <TextField
                        fullWidth
                        margin='normal'
                        label='Parking Fee (USD)'
                        value={parkingFee}
                        onChange={(e) => setParkingFee(e.target.value)}
                        type='number'
                    />
                    <Button
                        variant='contained'
                        onClick={async () => {
                            try {
                                const conversationRef = doc(
                                    db,
                                    'conversations',
                                    conversationId
                                );
                                await updateDoc(conversationRef, {
                                    parkingFee: parseFloat(parkingFee),
                                });
                                setIsFeeModalOpen(false);
                                setParkingFee('');
                                setHasParkingFee(true); // Update hasParkingFee state
                            } catch (error) {
                                console.error(
                                    'Error updating parking fee:',
                                    error
                                );
                            }
                        }}
                        sx={{ marginTop: 2, backgroundColor: '#113524' }}>
                        Update Fee
                    </Button>
                </Box>
            </Modal>

            {conversationStatus === 'pending' &&
            (userRole === 'customer' || userRole === 'guest') ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                    }}>
                    <Alert
                        severity='info'
                        sx={{
                            borderRadius: 3,
                            backgroundColor: '#F0F9FF',
                            color: '#0369A1',
                            fontSize: '1.2rem',
                            padding: '20px',
                        }}>
                        Waiting for the valet to accept the request...
                    </Alert>
                </Box>
            ) : (
                <>
                    {(userRole === 'customer' || isGuest) && collectOtp && !collectOtpVerified && otpModalDismissed && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 16,
                                left: 16,
                                right: 16,
                                zIndex: 1500,
                                backgroundColor: '#FFF8E1',
                                border: '1px solid #FFE082',
                                borderRadius: 2,
                                padding: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}>
                            <Typography sx={{ fontSize: '14px', color: '#7A5A00' }}>
                                OTP verification is pending.
                            </Typography>
                            <Button
                                variant='contained'
                                onClick={() => {
                                    setOtpModalDismissed(false);
                                    setIsModalOpen(true);
                                }}
                                sx={{ backgroundColor: '#113524' }}>
                                Verify OTP
                            </Button>
                        </Box>
                    )}
                    <Box
                        sx={{
                            height: 'calc(100vh - 200px)', // Increased to account for AppBar and input section
                            overflowY: 'auto',
                            pt: 8, // Increased top padding to prevent messages from going under header
                            pb:
                                userRole === 'customer' || userRole === 'guest'
                                    ? 2
                                    : 8, // Adjust padding based on user role
                            px: 2, // Add horizontal padding
                        }}>
                        {messages.map((message) => {
                            // Payment failure messaging: hide role-specific copies
                            if (
                                (userRole === 'customer' || isGuest) &&
                                message.messageType === 'payment_failed_valet'
                            ) {
                                return null;
                            }
                            if (
                                userRole === 'valet' &&
                                message.messageType === 'payment_failed_customer'
                            ) {
                                return null;
                            }

                            const isPaymentFailedMessage =
                                message?.type === 'PAYMENT_STATUS' &&
                                message?.status === 'failed';

                            const displayText =
                                isPaymentFailedMessage && message?.failureReason
                                    ? message.failureReason
                                    : message.text;

                            // Guest: hide collect-keys OTP (valet reads it to them verbally)
                            if (
                                (userRole === 'customer' || isGuest) &&
                                message.messageType === 'otp_collect_keys'
                            ) {
                                return null;
                            }

                            // Guest/Customer: hide "OTP sent" confirmation (valet-only)
                            if (
                                (userRole === 'customer' || isGuest) &&
                                message.messageType === 'otp_sent'
                            ) {
                                return null;
                            }

                            // Valet: hide return-keys OTP (guest reads it to them verbally)
                            if (
                                userRole === 'valet' &&
                                message.messageType === 'otp_verification'
                            ) {
                                return null;
                            }

                            // Valet: hide raw payment link
                            if (
                                userRole === 'valet' &&
                                message.messageType === 'payment_link'
                            ) {
                                return null;
                            }

                            return (
                                <MessageContainer
                                    key={message.id}
                                    isUser={
                                        message.senderId === 'system' || message.senderId === 'bot'
                                            ? false // System messages always on left
                                            : userRole === 'valet'
                                            ? message.senderId === currentUserId
                                            : message.senderId === currentUserId || 
                                              message.senderId === 'guest_user'
                                    }>
                                    <MessageBubble
                                        isUser={
                                            message.senderId === 'system' || message.senderId === 'bot'
                                                ? false // System messages always on left
                                                : userRole === 'valet'
                                                ? message.senderId === currentUserId
                                                : message.senderId === currentUserId ||
                                                  message.senderId === 'guest_user'
                                        }
                                        isSystem={
                                            message.senderId === 'system' || message.senderId === 'bot'
                                        }
                                        isError={isPaymentFailedMessage}>
                                        <SenderLabel>
                                            {message.senderId ===
                                            currentUserId
                                                ? 'You'
                                                : isPaymentFailedMessage
                                                ? 'Error'
                                                : message.senderId === 'bot' ||
                                                  message.senderId === 'system'
                                                ? 'System'
                                                : message.senderId ===
                                                  'guest_user'
                                                ? userRole === 'valet' ? 'Customer' : 'You'
                                                : userRole === 'valet' &&
                                                  message.text ===
                                                      'Please complete payment here:'
                                                ? 'You' // Show 'You' instead of 'Bot' for payment messages when user is valet
                                                : userRole === 'valet'
                                                ? 'Customer'
                                                : 'Valet'}
                                        </SenderLabel>
                                        <Typography
                                            variant='body1'
                                            sx={{ fontSize: '15px' }}>
                                            {renderMessageWithLinks(
                                                displayText
                                            )}
                                            {message.link &&
                                            !(typeof displayText === 'string' &&
                                                displayText.includes(message.link)) && (
                                                <a
                                                    href={message.link}
                                                    target='_blank'
                                                    rel='noopener noreferrer'>
                                                    {message.link}
                                                </a>
                                            )}
                                        </Typography>
                                    </MessageBubble>
                                </MessageContainer>
                            );
                        })}
                        {/* Empty div to scroll to the bottom */}
                        <div ref={messagesEndRef} />
                    </Box>

                    {conversationStatus === 'active' && (
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: '#fff',
                                p: 2,
                                borderTop: '1px solid #e0e0e0',
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mb: 2,
                                    alignItems: 'center',
                                }}>
                                <StyledTextField
                                    fullWidth
                                    placeholder='Type a message...'
                                    value={newMessage}
                                    onChange={(e) =>
                                        setNewMessage(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    variant='outlined'
                                />
                                <IconButton
                                    sx={{
                                        backgroundColor: '#113524',
                                        color: '#fff',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        '&:hover': {
                                            backgroundColor: '#0a2318',
                                        },
                                    }}
                                    onClick={handleSendMessage}>
                                    <SendIcon />
                                </IconButton>
                            </Box>

                            {userRole === 'customer' || userRole === 'guest' ? (
                                <div></div>
                            ) : (
                                userRole === 'valet' && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: 2,
                                        }}>
                                        <IconButton
                                            sx={{
                                                backgroundColor: hasParkingFee
                                                    ? '#113524'
                                                    : '#ccc',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor:
                                                        hasParkingFee
                                                            ? '#0a2318'
                                                            : '#ccc',
                                                },
                                                cursor: hasParkingFee
                                                    ? 'pointer'
                                                    : 'not-allowed',
                                            }}
                                            onClick={handleParkedCar}>
                                            <LocalParkingIcon />
                                        </IconButton>
                                        <IconButton
                                            sx={{
                                                backgroundColor: '#113524',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#0a2318',
                                                },
                                            }}
                                            onClick={() =>
                                                setIsFeeModalOpen(true)
                                            }>
                                            <AttachMoneyIcon />
                                        </IconButton>
                                        <IconButton
                                            sx={{
                                                backgroundColor: '#113524',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#0a2318',
                                                },
                                            }}
                                            onClick={handleReturningKeys}>
                                            <VpnKeyIcon />
                                        </IconButton>
                                        <IconButton
                                            sx={{
                                                backgroundColor: 'red',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#d32f2f',
                                                },
                                            }}
                                            onClick={() =>
                                                setIsConfirmationModalOpen(true)
                                            }>
                                            <CloseIcon />
                                        </IconButton>
                                        <IconButton
                                            sx={{
                                                backgroundColor: '#0099ff',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#007acc',
                                                },
                                            }}
                                            onClick={() =>
                                                setIsModalOpen(true)
                                            }>
                                            <VerifiedUserIcon />
                                        </IconButton>
                                    </Box>
                                )
                            )}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ActiveConversation;
